import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Plus, Edit2, Trash2, Users, TrendingUp, FileText, 
  DollarSign, Save, X, Check, ChevronDown, Upload, Download, Image, Copy, Bell, Search, Loader2, Mail, Phone, Clock, BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth, API } from "../App";
import Navbar, { AuthModal } from "../components/Navbar";
import { toast } from "sonner";
import axios from "axios";
import { SITUATIONAL_TAGS, TAG_COLORS } from "../utils/situationalTags";

const SPORTS = ["NFL", "NBA", "WNBA", "MLB", "NHL", "Soccer", "UFC", "NCAAF", "NCAAB", "Tennis", "PGA"];
const RESULTS = ["pending", "win", "loss", "push"];
const BET_AMOUNTS = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000];
const GAME_TIMES = [
  "12:00 PM", "12:05 PM", "12:10 PM", "12:15 PM", "12:20 PM", "12:25 PM", "12:30 PM", "12:35 PM", "12:40 PM", "12:45 PM", "12:50 PM", "12:55 PM",
  "1:00 PM", "1:05 PM", "1:10 PM", "1:15 PM", "1:20 PM", "1:25 PM", "1:30 PM", "1:35 PM", "1:40 PM", "1:45 PM", "1:50 PM", "1:55 PM",
  "2:00 PM", "2:05 PM", "2:10 PM", "2:15 PM", "2:20 PM", "2:25 PM", "2:30 PM", "2:35 PM", "2:40 PM", "2:45 PM", "2:50 PM", "2:55 PM",
  "3:00 PM", "3:05 PM", "3:10 PM", "3:15 PM", "3:20 PM", "3:25 PM", "3:30 PM", "3:35 PM", "3:40 PM", "3:45 PM", "3:50 PM", "3:55 PM",
  "4:00 PM", "4:05 PM", "4:10 PM", "4:15 PM", "4:20 PM", "4:25 PM", "4:30 PM", "4:35 PM", "4:40 PM", "4:45 PM", "4:50 PM", "4:55 PM",
  "5:00 PM", "5:05 PM", "5:10 PM", "5:15 PM", "5:20 PM", "5:25 PM", "5:30 PM", "5:35 PM", "5:40 PM", "5:45 PM", "5:50 PM", "5:55 PM",
  "6:00 PM", "6:05 PM", "6:10 PM", "6:15 PM", "6:20 PM", "6:25 PM", "6:30 PM", "6:35 PM", "6:40 PM", "6:45 PM", "6:50 PM", "6:55 PM",
  "7:00 PM", "7:05 PM", "7:10 PM", "7:15 PM", "7:20 PM", "7:25 PM", "7:30 PM", "7:35 PM", "7:40 PM", "7:45 PM", "7:50 PM", "7:55 PM",
  "8:00 PM", "8:05 PM", "8:10 PM", "8:15 PM", "8:20 PM", "8:25 PM", "8:30 PM", "8:35 PM", "8:40 PM", "8:45 PM", "8:50 PM", "8:55 PM",
  "9:00 PM", "9:05 PM", "9:10 PM", "9:15 PM", "9:20 PM", "9:25 PM", "9:30 PM", "9:35 PM", "9:40 PM", "9:45 PM", "9:50 PM", "9:55 PM",
  "10:00 PM", "10:05 PM", "10:10 PM", "10:15 PM", "10:20 PM", "10:25 PM", "10:30 PM", "10:35 PM", "10:40 PM", "10:45 PM", "10:50 PM", "10:55 PM",
  "11:00 PM", "11:05 PM", "11:10 PM", "11:15 PM", "11:20 PM", "11:25 PM", "11:30 PM", "11:35 PM", "11:40 PM", "11:45 PM", "11:50 PM", "11:55 PM",
  "12:00 AM", "12:05 AM", "12:10 AM", "12:15 AM", "12:20 AM", "12:25 AM", "12:30 AM", "12:35 AM", "12:40 AM", "12:45 AM", "12:50 AM", "12:55 AM",
  "1:00 AM", "1:05 AM", "1:10 AM", "1:15 AM", "1:20 AM", "1:25 AM", "1:30 AM", "1:35 AM", "1:40 AM", "1:45 AM", "1:50 AM", "1:55 AM",
  "2:00 AM", "2:05 AM", "2:10 AM", "2:15 AM", "2:20 AM", "2:25 AM", "2:30 AM", "2:35 AM", "2:40 AM", "2:45 AM", "2:50 AM", "2:55 AM",
  "3:00 AM", "3:05 AM", "3:10 AM", "3:15 AM", "3:20 AM", "3:25 AM", "3:30 AM", "3:35 AM", "3:40 AM", "3:45 AM", "3:50 AM", "3:55 AM"
];
const BLOG_CATEGORIES = ["NFL", "NBA", "WNBA", "MLB", "NHL", "Soccer", "UFC", "Strategy", "Analysis"];

const StatCard = ({ icon: Icon, label, value, color = "text-white" }) => (
  <div className="bg-hsl-surface border border-white/5 p-6 rounded-xl">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{label}</p>
        <p className={`font-mono text-3xl font-bold ${color}`}>{value}</p>
      </div>
      <Icon className="w-8 h-8 text-hsl-primary opacity-50" />
    </div>
  </div>
);

// Email Notification Status Component
const NotificationStatus = ({ notificationStats, onRefresh }) => {
  const [sending, setSending] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  useEffect(() => {
    const fetchSchedulerStatus = async () => {
      try {
        const headers = getAuthHeaders();
        const response = await axios.get(`${API}/admin/scheduler-status`, { headers });
        setSchedulerStatus(response.data);
      } catch (error) {
        console.warn("Could not fetch scheduler status");
      }
    };
    fetchSchedulerStatus();
  }, []);
  
  const handleTestEmail = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/test-email`);
      toast.success(response.data.message);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSending(false);
    }
  };

  const handleSendDigest = async (period) => {
    setSendingDigest(true);
    try {
      const response = await axios.post(`${API}/admin/send-digest?period=${period}`);
      toast.success(`${period === 'daily' ? 'Daily' : 'Weekly'} digest sent to ${response.data.sent} subscribers`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };
  
  if (!notificationStats) return null;
  
  const emailConfigured = notificationStats.email_configured;
  const smsConfigured = notificationStats.sms_configured;
  
  return (
    <div className="mb-6 space-y-4">
      {/* Email Status */}
      <div className={`p-4 rounded-xl border ${emailConfigured ? 'bg-green-500/5 border-green-500/20' : 'bg-yellow-500/5 border-yellow-500/20'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${emailConfigured ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
              <Mail className={`w-5 h-5 ${emailConfigured ? 'text-green-400' : 'text-yellow-400'}`} />
            </div>
            <div>
              <p className="text-white font-medium">
                Email: {emailConfigured ? (
                  <span className="text-green-400">Configured</span>
                ) : (
                  <span className="text-yellow-400">Not Configured</span>
                )}
              </p>
              <p className="text-zinc-500 text-sm">
                {emailConfigured 
                  ? `${notificationStats.sender_email} • ${notificationStats.email_subscribers} subs • ${notificationStats.daily_digest_subscribers} daily / ${notificationStats.weekly_digest_subscribers} weekly digest`
                  : 'Add RESEND_API_KEY to enable'
                }
              </p>
            </div>
          </div>
          {emailConfigured && (
            <div className="flex gap-2">
              <Button
                onClick={handleTestEmail}
                disabled={sending}
                variant="outline"
                size="sm"
                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                data-testid="test-email-btn"
              >
                {sending ? "..." : "Test Email"}
              </Button>
              <Button
                onClick={() => handleSendDigest('daily')}
                disabled={sendingDigest}
                variant="outline"
                size="sm"
                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              >
                Send Daily
              </Button>
              <Button
                onClick={() => handleSendDigest('weekly')}
                disabled={sendingDigest}
                variant="outline"
                size="sm"
                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              >
                Send Weekly
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* SMS Status */}
      <div className={`p-4 rounded-xl border ${smsConfigured ? 'bg-blue-500/5 border-blue-500/20' : 'bg-yellow-500/5 border-yellow-500/20'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${smsConfigured ? 'bg-blue-500/20' : 'bg-yellow-500/20'}`}>
              <Phone className={`w-5 h-5 ${smsConfigured ? 'text-blue-400' : 'text-yellow-400'}`} />
            </div>
            <div>
              <p className="text-white font-medium">
                SMS: {smsConfigured ? (
                  <span className="text-blue-400">Configured</span>
                ) : (
                  <span className="text-yellow-400">Not Configured</span>
                )}
              </p>
              <p className="text-zinc-500 text-sm">
                {smsConfigured 
                  ? `${notificationStats.twilio_phone} • ${notificationStats.sms_subscribers} subscribers`
                  : 'Add TWILIO credentials to enable'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Automated Scheduler Status */}
      {schedulerStatus && schedulerStatus.scheduler_running && (
        <div className="p-4 rounded-xl border bg-purple-500/5 border-purple-500/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-white font-medium">
                Automated Digests: <span className="text-purple-400">Active</span>
              </p>
              <p className="text-zinc-500 text-sm">
                Emails sent automatically at 9 AM ET
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-11">
            {schedulerStatus.jobs.map(job => (
              <div key={job.id} className="text-xs bg-white/5 rounded-lg px-3 py-2">
                <span className="text-zinc-400">{job.name}:</span>
                <span className="text-purple-300 ml-1">
                  {job.next_run ? new Date(job.next_run).toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  }) : 'Not scheduled'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PickForm = ({ pick, onSave, onCancel }) => {
  // Helper to extract date and time from ISO string (handles timezone properly)
  const parseDateTime = (isoString) => {
    if (!isoString) {
      const now = new Date();
      return { 
        date: now.toISOString().slice(0, 10), 
        time: "7:00 PM" 
      };
    }
    
    // Parse the ISO string and convert to ET timezone for display using Intl
    const d = new Date(isoString);
    
    // Use Intl.DateTimeFormat to get parts in America/New_York timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const parts = formatter.formatToParts(d);
    const getPart = (type) => parts.find(p => p.type === type)?.value || '';
    
    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');
    const hour = getPart('hour');
    const minute = getPart('minute');
    const dayPeriod = getPart('dayPeriod').toUpperCase();
    
    const date = `${year}-${month}-${day}`;
    const time = `${parseInt(hour, 10)}:${minute} ${dayPeriod}`;
    
    return { date, time };
  };

  // Helper to combine date and time into ISO string
  // Time entered is assumed to be Eastern Time (ET)
  const combineDateTime = (date, time) => {
    // Parse the time string (e.g., "7:15 PM")
    const timeMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) {
      console.warn("Time format not recognized:", time);
      return new Date(`${date}T19:00:00Z`).toISOString();
    }
    
    let [, hours, minutes, ampm] = timeMatch;
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);
    
    if (ampm.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (ampm.toUpperCase() === "AM" && hours === 12) hours = 0;
    
    // Create a UTC timestamp for the entered date/time
    const [year, month, day] = date.split('-').map(Number);
    const asUTC = Date.UTC(year, month - 1, day, hours, minutes, 0);
    
    // Get what this UTC time would be in America/New_York
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    const parts = formatter.formatToParts(new Date(asUTC));
    const getPart = (type) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    
    // Create a UTC timestamp for what America/New_York would show for asUTC
    const asET = Date.UTC(
      getPart('year'),
      getPart('month') - 1,
      getPart('day'),
      getPart('hour') % 24,
      getPart('minute'),
      getPart('second')
    );
    
    // The difference tells us the offset, apply it inversely to get correct UTC
    const correctUTC = asUTC + (asUTC - asET);
    
    return new Date(correctUTC).toISOString();
  };

  // Initialize form data - preserve original values when editing
  const getInitialFormData = () => {
    if (pick && pick.game_time) {
      const { date, time } = parseDateTime(pick.game_time);
      return {
        ...pick,
        bet_amount: pick.bet_amount || pick.units || 100,
        game_date: date,
        game_time_select: time,
        send_notification: false,
        send_sms: false,
        closing_line: pick.closing_line || "",
        is_prop: pick.is_prop || false
      };
    }
    // New pick - use today's date and default time
    const today = new Date();
    const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return {
      sport: "NFL",
      league: "NFL",
      matchup: "",
      pick: "",
      odds: "",
      bet_amount: 100,
      analysis: "",
      pick_tier: "free",
      game_date: date,
      game_time_select: "7:00 PM",
      result: "pending",
      send_notification: false,
      send_sms: false,
      away_score: "",
      home_score: "",
      prop_result: "",
      pick_image_url: "",
      closing_line: "",
      situational_tags: [],
      is_prop: false
    };
  };

  const [formData, setFormData] = useState(() => getInitialFormData());
  const [uploadingPickImage, setUploadingPickImage] = useState(false);
  const pickImageInputRef = useRef(null);

  // Get auth headers for API calls
  const getAuthHeaders = () => {
    const savedToken = localStorage.getItem("hsl_token");
    return savedToken ? { Authorization: `Bearer ${savedToken}` } : {};
  };

  // Handle pick image upload for Hammer Collection
  const handlePickImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPickImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers
      });
      
      setFormData(prev => ({ ...prev, pick_image_url: response.data.url }));
      toast.success("Image uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingPickImage(false);
    }
  };

  // Reset form when pick changes (when opening edit modal for different pick)
  useEffect(() => {
    if (pick) {
      const { date, time } = parseDateTime(pick.game_time);
      setFormData(prev => ({
        ...prev,
        ...pick,
        bet_amount: pick.bet_amount || pick.units || 100,
        game_date: date,
        game_time_select: time,
        send_notification: false,
        send_sms: false,
        away_score: pick.away_score || "",
        home_score: pick.home_score || "",
        prop_result: pick.prop_result || "",
        pick_image_url: pick.pick_image_url || "",
        closing_line: pick.closing_line || "",
        situational_tags: pick.situational_tags || []
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick?.id]); // Only re-run when pick ID changes

  // Odds fetching state
  const [availableGames, setAvailableGames] = useState([]);
  const [playerProps, setPlayerProps] = useState([]);
  const [loadingOdds, setLoadingOdds] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [oddsError, setOddsError] = useState(null);
  const [showProps, setShowProps] = useState(false);

  // Auto-enable notifications for Hammer picks
  useEffect(() => {
    if (formData.pick_tier === 'hammer' && !pick) {
      setFormData(prev => ({ ...prev, send_notification: true, send_sms: true }));
    }
  }, [formData.pick_tier, pick]);

  // Fetch available games when sport changes
  const fetchGamesWithOdds = async () => {
    setLoadingOdds(true);
    setOddsError(null);
    setAvailableGames([]);
    setPlayerProps([]);
    setSelectedGame(null);
    setShowProps(false);
    
    try {
      const response = await axios.get(`${API}/odds/${formData.sport}`);
      if (response.data.games && response.data.games.length > 0) {
        setAvailableGames(response.data.games);
      } else {
        setOddsError(`No upcoming games found for ${formData.sport}`);
      }
      // Set player props if available
      if (response.data.player_props && response.data.player_props.length > 0) {
        setPlayerProps(response.data.player_props);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to fetch odds";
      setOddsError(message);
    } finally {
      setLoadingOdds(false);
    }
  };

  // Handle game selection from dropdown
  const handleGameSelect = (gameId) => {
    const game = availableGames.find(g => g.id === gameId);
    if (game) {
      setSelectedGame(game);
      
      // Parse commence time
      const gameTime = new Date(game.commence_time);
      const { date, time } = parseDateTime(game.commence_time);
      
      setFormData(prev => ({
        ...prev,
        matchup: game.matchup,
        game_date: date,
        game_time_select: time
      }));
    }
  };

  // Apply selected odds type to form
  const applyOdds = (type, value, oddsValue) => {
    let pickText = "";
    let oddsText = oddsValue?.toString() || "-110";
    
    if (type === "spread_home" && selectedGame) {
      pickText = `${selectedGame.home_team} ${value > 0 ? '+' : ''}${value}`;
    } else if (type === "spread_away" && selectedGame) {
      const awaySpread = value * -1;
      pickText = `${selectedGame.away_team} ${awaySpread > 0 ? '+' : ''}${awaySpread}`;
    } else if (type === "ml_home" && selectedGame) {
      pickText = `${selectedGame.home_team} ML`;
      oddsText = value?.toString() || "-110";
    } else if (type === "ml_away" && selectedGame) {
      pickText = `${selectedGame.away_team} ML`;
      oddsText = value?.toString() || "-110";
    } else if (type === "over" && selectedGame) {
      pickText = `Over ${selectedGame.odds.total.points}`;
      oddsText = value?.toString() || "-110";
    } else if (type === "under" && selectedGame) {
      pickText = `Under ${selectedGame.odds.total.points}`;
      oddsText = value?.toString() || "-110";
    }
    
    setFormData(prev => ({
      ...prev,
      pick: pickText,
      odds: oddsText
    }));
  };

  // Apply player prop to form
  const applyPlayerProp = (prop) => {
    const pickText = `${prop.player} ${prop.name} ${prop.line} ${prop.market_display}`;
    const oddsText = prop.odds?.toString() || "-110";
    
    // Parse commence time for the prop
    const { date, time } = parseDateTime(prop.commence_time);
    
    setFormData(prev => ({
      ...prev,
      matchup: prop.matchup,
      pick: pickText,
      odds: oddsText,
      game_date: date,
      game_time_select: time
    }));
    setShowProps(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      sport: formData.sport,
      league: formData.league,
      matchup: formData.matchup,
      pick: formData.pick,
      odds: formData.odds,
      bet_amount: parseInt(formData.bet_amount),
      analysis: formData.analysis,
      pick_tier: formData.pick_tier,
      game_time: combineDateTime(formData.game_date, formData.game_time_select),
      result: formData.result,
      send_notification: !pick && formData.send_notification,  // Only send notifications for new picks
      send_sms: !pick && formData.send_sms,  // Only send SMS for new picks
      away_score: formData.away_score ? parseInt(formData.away_score) : null,
      home_score: formData.home_score ? parseInt(formData.home_score) : null,
      prop_result: formData.prop_result || null,  // Single score for props (e.g., "28 saves")
      pick_image_url: formData.pick_image_url || null,  // Include image URL for Hammer picks
      closing_line: formData.closing_line || null,  // Closing line for CLV tracking
      situational_tags: formData.situational_tags || [],  // Edge tags
      is_prop: formData.is_prop || false  // Prop bet flag
    };
    onSave(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-400">Sport</Label>
          <Select value={formData.sport} onValueChange={(v) => {
            setFormData({...formData, sport: v, league: v});
            setAvailableGames([]);
            setSelectedGame(null);
            setOddsError(null);
          }}>
            <SelectTrigger className="input-hsl mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPORTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-400">Game Date</Label>
          <Input
            type="date"
            value={formData.game_date}
            onChange={(e) => setFormData({...formData, game_date: e.target.value})}
            className="input-hsl mt-1"
            required
          />
        </div>
        <div>
          <Label className="text-zinc-400">Game Time (ET)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              type="text"
              value={formData.game_time_select}
              onChange={(e) => setFormData({...formData, game_time_select: e.target.value})}
              placeholder="e.g. 7:15 PM"
              className="input-hsl flex-1"
              data-testid="game-time-input"
            />
            <Select value="" onValueChange={(v) => setFormData({...formData, game_time_select: v})}>
              <SelectTrigger className="input-hsl w-24" data-testid="game-time-select-trigger">
                <SelectValue placeholder="Pick" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {GAME_TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-zinc-500 text-xs mt-1">Type any time or select from dropdown</p>
        </div>
      </div>
      
      {/* Odds Fetcher Section */}
      {!pick && (
        <div className="bg-hsl-bg/50 border border-white/10 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-white font-semibold">Fetch Live Odds</Label>
              <p className="text-zinc-500 text-xs">Auto-populate from The Odds API, or enter manually below</p>
            </div>
            <Button 
              type="button" 
              onClick={fetchGamesWithOdds}
              disabled={loadingOdds}
              variant="outline"
              className="bg-hsl-primary/20 border-hsl-primary text-hsl-primary hover:bg-hsl-primary hover:text-black"
              data-testid="fetch-odds-btn"
            >
              {loadingOdds ? "Loading..." : "Fetch Games"}
            </Button>
          </div>
          
          {oddsError && (
            <p className="text-yellow-400 text-sm">{oddsError} — Enter details manually below.</p>
          )}
          
          {availableGames.length > 0 && (
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-400">Select Game</Label>
                <Select onValueChange={handleGameSelect}>
                  <SelectTrigger className="input-hsl mt-1">
                    <SelectValue placeholder="Choose a game..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {availableGames.map(game => (
                      <SelectItem key={game.id} value={game.id}>
                        {game.matchup} — {new Date(game.commence_time).toLocaleString('en-US', { 
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                        })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedGame && (
                <div className="bg-hsl-surface rounded-lg p-3 space-y-2">
                  <p className="text-white font-semibold text-sm">{selectedGame.matchup}</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {/* Spread */}
                    {selectedGame.odds.spread.home !== null && (
                      <div>
                        <p className="text-zinc-500 mb-1">Spread</p>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline"
                          className="w-full text-xs py-1 px-2 border-zinc-600 hover:border-hsl-primary hover:bg-hsl-primary/20"
                          onClick={() => applyOdds("spread_home", selectedGame.odds.spread.home, selectedGame.odds.spread.odds)}
                        >
                          {selectedGame.home_team.split(' ').pop()} {selectedGame.odds.spread.home > 0 ? '+' : ''}{selectedGame.odds.spread.home} ({selectedGame.odds.spread.odds})
                        </Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline"
                          className="w-full text-xs py-1 px-2 mt-1 border-zinc-600 hover:border-hsl-primary hover:bg-hsl-primary/20"
                          onClick={() => applyOdds("spread_away", selectedGame.odds.spread.home, selectedGame.odds.spread.odds)}
                        >
                          {selectedGame.away_team.split(' ').pop()} {(selectedGame.odds.spread.home * -1) > 0 ? '+' : ''}{selectedGame.odds.spread.home * -1} ({selectedGame.odds.spread.odds})
                        </Button>
                      </div>
                    )}
                    
                    {/* Moneyline */}
                    {(selectedGame.odds.moneyline.home !== null || selectedGame.odds.moneyline.away !== null) && (
                      <div>
                        <p className="text-zinc-500 mb-1">Moneyline</p>
                        {selectedGame.odds.moneyline.home !== null && (
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            className="w-full text-xs py-1 px-2 border-zinc-600 hover:border-hsl-primary hover:bg-hsl-primary/20"
                            onClick={() => applyOdds("ml_home", selectedGame.odds.moneyline.home)}
                          >
                            {selectedGame.home_team.split(' ').pop()} ({selectedGame.odds.moneyline.home > 0 ? '+' : ''}{selectedGame.odds.moneyline.home})
                          </Button>
                        )}
                        {selectedGame.odds.moneyline.away !== null && (
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            className="w-full text-xs py-1 px-2 mt-1 border-zinc-600 hover:border-hsl-primary hover:bg-hsl-primary/20"
                            onClick={() => applyOdds("ml_away", selectedGame.odds.moneyline.away)}
                          >
                            {selectedGame.away_team.split(' ').pop()} ({selectedGame.odds.moneyline.away > 0 ? '+' : ''}{selectedGame.odds.moneyline.away})
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Total */}
                    {selectedGame.odds.total.points !== null && (
                      <div>
                        <p className="text-zinc-500 mb-1">Total ({selectedGame.odds.total.points})</p>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline"
                          className="w-full text-xs py-1 px-2 border-zinc-600 hover:border-hsl-primary hover:bg-hsl-primary/20"
                          onClick={() => applyOdds("over", selectedGame.odds.total.over_odds)}
                        >
                          Over ({selectedGame.odds.total.over_odds})
                        </Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline"
                          className="w-full text-xs py-1 px-2 mt-1 border-zinc-600 hover:border-hsl-primary hover:bg-hsl-primary/20"
                          onClick={() => applyOdds("under", selectedGame.odds.total.under_odds)}
                        >
                          Under ({selectedGame.odds.total.under_odds})
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">Click an option to auto-fill Pick & Odds below</p>
                </div>
              )}
            </div>
          )}
          
          {/* Player Props Section - Only for NBA, MLB, NHL */}
          {playerProps.length > 0 && ['NBA', 'MLB', 'NHL'].includes(formData.sport) && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-white font-semibold">Player Props</Label>
                  <p className="text-zinc-500 text-xs">{playerProps.length} props available</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-zinc-600 text-zinc-300 hover:bg-hsl-primary/20"
                  onClick={() => setShowProps(!showProps)}
                >
                  {showProps ? 'Hide Props' : 'Show Props'}
                </Button>
              </div>
              
              {showProps && (
                <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                  {/* Group props by market type */}
                  {Object.entries(
                    playerProps.reduce((acc, prop) => {
                      const market = prop.market_display;
                      if (!acc[market]) acc[market] = [];
                      acc[market].push(prop);
                      return acc;
                    }, {})
                  ).map(([market, props]) => (
                    <div key={market} className="bg-hsl-surface/50 rounded-lg p-3">
                      <p className="text-hsl-primary text-xs font-bold uppercase mb-2">{market}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {props.slice(0, 8).map((prop, idx) => (
                          <Button
                            key={`${prop.player}-${prop.line}-${idx}`}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full text-xs py-2 px-2 border-zinc-700 hover:border-hsl-primary hover:bg-hsl-primary/20 text-left justify-start"
                            onClick={() => applyPlayerProp(prop)}
                          >
                            <div className="truncate">
                              <span className="text-white">{prop.player}</span>
                              <span className="text-zinc-400 ml-1">
                                {prop.name} {prop.line} ({prop.odds > 0 ? '+' : ''}{prop.odds})
                              </span>
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      <div>
        <Label className="text-zinc-400">Matchup <span className="text-zinc-600 text-xs">(Away @ Home)</span></Label>
        <Input
          value={formData.matchup}
          onChange={(e) => setFormData({...formData, matchup: e.target.value})}
          placeholder="Away Team @ Home Team"
          className="input-hsl mt-1"
          required
        />
        <p className="text-zinc-600 text-xs mt-1">Format: Away @ Home (team on right is always home)</p>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-400">Pick</Label>
          <Input
            value={formData.pick}
            onChange={(e) => setFormData({...formData, pick: e.target.value})}
            placeholder="Team -3.5"
            className="input-hsl mt-1"
            required
          />
        </div>
        <div>
          <Label className="text-zinc-400">Odds</Label>
          <Input
            value={formData.odds}
            onChange={(e) => setFormData({...formData, odds: e.target.value})}
            placeholder="-110"
            className="input-hsl mt-1"
            required
          />
        </div>
        <div>
          <Label className="text-zinc-400">Bet Amount ($)</Label>
          <Select value={formData.bet_amount.toString()} onValueChange={(v) => setFormData({...formData, bet_amount: parseInt(v)})}>
            <SelectTrigger className="input-hsl mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BET_AMOUNTS.map((amount) => (
                <SelectItem key={amount} value={amount.toString()}>
                  ${amount}{amount === 2000 ? ' (Full Hammer)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label className="text-zinc-400">Analysis</Label>
        <Textarea
          value={formData.analysis}
          onChange={(e) => setFormData({...formData, analysis: e.target.value})}
          placeholder="Why is this a good play..."
          className="input-hsl mt-1 min-h-[100px]"
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400">Pick Tier</Label>
          <Select value={formData.pick_tier} onValueChange={(v) => setFormData({...formData, pick_tier: v})}>
            <SelectTrigger className="input-hsl mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="premium">Members Only</SelectItem>
              <SelectItem value="hammer">🔨 HAMMER</SelectItem>
              <SelectItem value="scheduled_loss">📅 Scheduled Loss</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {pick && (
          <div>
            <Label className="text-zinc-400">Result</Label>
            <Select value={formData.result} onValueChange={(v) => setFormData({...formData, result: v})}>
              <SelectTrigger className="input-hsl mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESULTS.map(r => <SelectItem key={r} value={r}>{r.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      {/* Is Prop Checkbox */}
      <div className="bg-hsl-surface/50 border border-white/10 rounded-lg p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={formData.is_prop || false}
            onCheckedChange={(checked) => setFormData({...formData, is_prop: checked})}
            data-testid="is-prop-checkbox"
          />
          <div className="flex-1">
            <span className="text-white font-semibold">This is a Prop Bet</span>
            <p className="text-zinc-500 text-xs mt-1">
              Mark this pick as a player/team prop (saves, strikeouts, points, etc.) to track prop performance separately
            </p>
          </div>
        </label>
      </div>
      
      {/* Situational Edge Tags */}
      <div className="bg-hsl-surface/50 border border-white/10 rounded-lg p-4">
        <Label className="text-zinc-400 font-semibold block mb-3">Situational Edge Tags (Optional)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(SITUATIONAL_TAGS).map(([code, tag]) => (
            <label 
              key={code}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                formData.situational_tags?.includes(code)
                  ? TAG_COLORS[code] + ' border-current'
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <Checkbox
                checked={formData.situational_tags?.includes(code)}
                onCheckedChange={(checked) => {
                  const current = formData.situational_tags || [];
                  const updated = checked 
                    ? [...current, code]
                    : current.filter(t => t !== code);
                  setFormData({...formData, situational_tags: updated});
                }}
              />
              <div className="flex-1">
                <span className="font-mono font-bold text-xs">[{code}]</span>
                <span className="text-zinc-400 text-xs ml-1">{tag.name}</span>
              </div>
            </label>
          ))}
        </div>
        <p className="text-zinc-600 text-xs mt-2">Select factors that give this pick an edge</p>
      </div>
      
      {/* Final Scores - Show when editing or when result is not pending */}
      {pick && (
        <div className="bg-hsl-surface/50 border border-white/10 rounded-lg p-4">
          <Label className="text-zinc-400 font-semibold block mb-3">Final Score / Prop Result</Label>
          
          {/* Prop Result - Single number for player props */}
          <div className="mb-4">
            <Label className="text-zinc-500 text-xs">Prop Result (for player props like saves, strikeouts, etc.)</Label>
            <Input
              type="text"
              placeholder="e.g., 28 saves, 7 strikeouts"
              value={formData.prop_result || ""}
              onChange={(e) => setFormData({...formData, prop_result: e.target.value})}
              className="input-hsl mt-1"
            />
          </div>
          
          <div className="border-t border-white/10 pt-4 mt-4">
            <Label className="text-zinc-500 text-xs block mb-2">OR Game Score (for spread/ML/totals)</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-500 text-xs">Away Score</Label>
                <Input
                  type="number"
                  placeholder="Away"
                  value={formData.away_score}
                  onChange={(e) => setFormData({...formData, away_score: e.target.value})}
                  className="input-hsl mt-1"
                  min="0"
                />
              </div>
              <div>
                <Label className="text-zinc-500 text-xs">Home Score</Label>
                <Input
                  type="number"
                  placeholder="Home"
                  value={formData.home_score}
                  onChange={(e) => setFormData({...formData, home_score: e.target.value})}
                  className="input-hsl mt-1"
                  min="0"
                />
              </div>
            </div>
          </div>
          <p className="text-zinc-600 text-xs mt-2">Use Prop Result for single stats, or Away/Home for game scores</p>
        </div>
      )}
      
      {/* Closing Line for CLV Tracking - Show when editing */}
      {pick && (
        <div className="bg-hsl-surface/50 border border-white/10 rounded-lg p-4">
          <Label className="text-zinc-400 font-semibold block mb-3">Closing Line (CLV Tracking)</Label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-500 text-xs">Your Line</Label>
              <Input
                type="text"
                value={formData.odds}
                disabled
                className="input-hsl mt-1 opacity-60"
              />
            </div>
            <div>
              <Label className="text-zinc-500 text-xs">Closing Line</Label>
              <Input
                type="text"
                placeholder="-230"
                value={formData.closing_line}
                onChange={(e) => setFormData({...formData, closing_line: e.target.value})}
                className="input-hsl mt-1"
              />
            </div>
          </div>
          <p className="text-zinc-600 text-xs mt-2">Enter what the line closed at to track CLV (Closing Line Value)</p>
        </div>
      )}
      
      {/* Hammer Pick Image Upload - Only for Hammer picks */}
      {formData.pick_tier === 'hammer' && (
        <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg p-4">
          <Label className="text-yellow-400 font-semibold block mb-3 flex items-center gap-2">
            <span>🔨</span> Hammer Collection Image
          </Label>
          <p className="text-zinc-500 text-xs mb-3">Upload an image for the Hammer Collection page (optional)</p>
          
          <input
            type="file"
            ref={pickImageInputRef}
            onChange={handlePickImageUpload}
            accept="image/*"
            className="hidden"
          />
          
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => pickImageInputRef.current?.click()}
              disabled={uploadingPickImage}
              className="bg-yellow-500/10 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20"
            >
              {uploadingPickImage ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload Image</>
              )}
            </Button>
            
            {formData.pick_image_url && (
              <div className="flex items-center gap-2">
                <img 
                  src={formData.pick_image_url.startsWith('http') ? formData.pick_image_url : `${API.replace('/api', '')}${formData.pick_image_url}`}
                  alt="Pick preview"
                  className="h-12 w-20 object-cover rounded border border-yellow-500/30"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, pick_image_url: "" }))}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Notification Toggles - Only show for new picks */}
      {!pick && (
        <div className="space-y-3">
          {/* Email Notification Toggle */}
          <div className="flex items-center justify-between bg-hsl-surface/50 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${formData.send_notification ? 'bg-hsl-primary/20' : 'bg-white/5'}`}>
                <svg className={`w-5 h-5 ${formData.send_notification ? 'text-hsl-primary' : 'text-zinc-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium text-sm">Email Subscribers</p>
                <p className="text-zinc-500 text-xs">Notify users who opted in to email alerts</p>
              </div>
            </div>
            <Switch
              checked={formData.send_notification}
              onCheckedChange={(checked) => setFormData({...formData, send_notification: checked})}
              data-testid="send-notification-switch"
            />
          </div>

          {/* SMS Notification Toggle */}
          <div className="flex items-center justify-between bg-hsl-surface/50 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${formData.send_sms ? 'bg-green-500/20' : 'bg-white/5'}`}>
                <svg className={`w-5 h-5 ${formData.send_sms ? 'text-green-500' : 'text-zinc-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium text-sm">SMS Subscribers</p>
                <p className="text-zinc-500 text-xs">Send text message to users with SMS enabled</p>
              </div>
            </div>
            <Switch
              checked={formData.send_sms}
              onCheckedChange={(checked) => setFormData({...formData, send_sms: checked})}
              data-testid="send-sms-switch"
            />
          </div>
        </div>
      )}
      
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="btn-primary-glow flex-1">
          <Save className="w-4 h-4 mr-2" />
          {pick ? "Update Pick" : "Create Pick"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="bg-white/5 border-white/10">
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </form>
  );
};

const BlogForm = ({ post, onSave, onCancel }) => {
  const [formData, setFormData] = useState(post || {
    title: "",
    excerpt: "",
    content: "",
    category: "Strategy",
    image_url: "",
    audio_url: ""
  });
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: JPEG, PNG, GIF, WEBP");
      return;
    }
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded!");
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/x-m4a', 'audio/mp4'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: MP3, WAV, OGG, M4A");
      return;
    }
    
    setUploadingAudio(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/upload-audio`, formDataUpload, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, audio_url: response.data.url }));
      toast.success("Audio uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload audio");
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-zinc-400">Title</Label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          placeholder="Article title..."
          className="input-hsl mt-1"
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400">Category</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
            <SelectTrigger className="input-hsl mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BLOG_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-400">Featured Image (optional)</Label>
          <div className="mt-1 flex items-center gap-3">
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploadingImage}
              className="bg-white/5 border-white/10 text-white"
            >
              {uploadingImage ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload Image</>
              )}
            </Button>
            {formData.image_url && (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <Check className="w-4 h-4" />
                <span>Image attached</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {formData.image_url && (
            <img 
              src={formData.image_url} 
              alt="Preview" 
              className="mt-2 h-24 object-cover rounded-lg"
            />
          )}
        </div>
      </div>
      
      {/* Audio Upload */}
      <div>
        <Label className="text-zinc-400">Audio File (optional - for podcasts)</Label>
        <div className="mt-1 flex items-center gap-3">
          <input
            type="file"
            ref={audioInputRef}
            onChange={handleAudioUpload}
            accept="audio/*"
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => audioInputRef.current?.click()}
            disabled={uploadingAudio}
            className="bg-white/5 border-white/10 text-white"
          >
            {uploadingAudio ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload Audio</>
            )}
          </Button>
          {formData.audio_url && (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <Check className="w-4 h-4" />
              <span>Audio attached</span>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, audio_url: "" }))}
                className="text-red-400 hover:text-red-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        {formData.audio_url && (
          <audio controls className="mt-2 w-full" src={`${API.replace('/api', '')}${formData.audio_url}`}>
            Your browser does not support the audio element.
          </audio>
        )}
      </div>
      
      <div>
        <Label className="text-zinc-400">Excerpt</Label>
        <Textarea
          value={formData.excerpt}
          onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
          placeholder="Brief summary for preview..."
          className="input-hsl mt-1"
          required
        />
      </div>
      
      <div>
        <Label className="text-zinc-400">Content</Label>
        <Textarea
          value={formData.content}
          onChange={(e) => setFormData({...formData, content: e.target.value})}
          placeholder="Full article content..."
          className="input-hsl mt-1 min-h-[200px]"
          required
        />
      </div>
      
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="btn-primary-glow flex-1">
          <Save className="w-4 h-4 mr-2" />
          {post ? "Update Post" : "Create Post"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="bg-white/5 border-white/10">
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Performance Audit Component for Situational Tags
const PerformanceAudit = () => {
  const [tagStats, setTagStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/stats/situational-tags`, { headers });
        setTagStats(response.data.tags || []);
      } catch (error) {
        console.warn('Failed to fetch tag stats:', error);
        toast.error('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1,2,3,4,5,6].map(i => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const hasData = tagStats.some(t => t.total_picks > 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-hsl-surface rounded-xl p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Total Tagged Picks</p>
          <p className="text-2xl font-bold text-white mt-1">
            {tagStats.reduce((sum, t) => sum + t.total_picks, 0)}
          </p>
        </div>
        <div className="bg-hsl-surface rounded-xl p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Best Win Rate</p>
          <p className="text-2xl font-bold text-green-400 mt-1">
            {hasData ? Math.max(...tagStats.filter(t => t.total_picks > 0).map(t => t.win_rate)) + '%' : '-'}
          </p>
        </div>
        <div className="bg-hsl-surface rounded-xl p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Best ROI</p>
          <p className="text-2xl font-bold text-green-400 mt-1">
            {hasData ? Math.max(...tagStats.filter(t => t.total_picks > 0).map(t => t.roi)) + '%' : '-'}
          </p>
        </div>
        <div className="bg-hsl-surface rounded-xl p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Total Profit (Tagged)</p>
          <p className={`text-2xl font-bold mt-1 ${tagStats.reduce((sum, t) => sum + t.profit, 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${tagStats.reduce((sum, t) => sum + t.profit, 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Tag Performance Table */}
      <div className="bg-hsl-surface rounded-xl border border-white/5 overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="font-heading font-bold text-white uppercase">Performance by Situational Tag</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left text-xs text-zinc-500 uppercase tracking-wide px-4 py-3">Tag</th>
                <th className="text-center text-xs text-zinc-500 uppercase tracking-wide px-4 py-3">Picks</th>
                <th className="text-center text-xs text-zinc-500 uppercase tracking-wide px-4 py-3">Record</th>
                <th className="text-center text-xs text-zinc-500 uppercase tracking-wide px-4 py-3">Win Rate</th>
                <th className="text-center text-xs text-zinc-500 uppercase tracking-wide px-4 py-3">ROI</th>
                <th className="text-right text-xs text-zinc-500 uppercase tracking-wide px-4 py-3">Profit</th>
                <th className="text-center text-xs text-zinc-500 uppercase tracking-wide px-4 py-3">Hammer W%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {tagStats.map((tag) => (
                <tr key={tag.code} className={`hover:bg-white/5 transition-colors ${tag.total_picks === 0 ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-bold text-sm px-2 py-0.5 rounded border ${TAG_COLORS[tag.code]}`}>
                        [{tag.code}]
                      </span>
                      <span className="text-white text-sm">{tag.name}</span>
                    </div>
                  </td>
                  <td className="text-center text-white px-4 py-4">{tag.total_picks}</td>
                  <td className="text-center text-white px-4 py-4">
                    {tag.total_picks > 0 ? `${tag.wins}-${tag.losses}` : '-'}
                  </td>
                  <td className="text-center px-4 py-4">
                    <span className={tag.win_rate >= 55 ? 'text-green-400' : tag.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'}>
                      {tag.total_picks > 0 ? `${tag.win_rate}%` : '-'}
                    </span>
                  </td>
                  <td className="text-center px-4 py-4">
                    <span className={tag.roi >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {tag.total_picks > 0 ? `${tag.roi > 0 ? '+' : ''}${tag.roi}%` : '-'}
                    </span>
                  </td>
                  <td className="text-right px-4 py-4">
                    <span className={tag.profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {tag.total_picks > 0 ? `${tag.profit >= 0 ? '+' : ''}$${tag.profit.toLocaleString()}` : '-'}
                    </span>
                  </td>
                  <td className="text-center px-4 py-4">
                    {tag.hammer_win_rate !== null ? (
                      <span className={tag.hammer_win_rate >= 60 ? 'text-green-400' : 'text-zinc-400'}>
                        {tag.hammer_win_rate}%
                      </span>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!hasData && (
          <div className="p-8 text-center">
            <p className="text-zinc-500">No picks with situational tags yet.</p>
            <p className="text-zinc-600 text-sm mt-1">Add tags when creating picks to track performance.</p>
          </div>
        )}
      </div>

      {/* Tag Legend */}
      <div className="bg-hsl-surface/50 rounded-xl border border-white/5 p-4">
        <h4 className="text-sm font-semibold text-zinc-400 mb-3">Tag Reference</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(SITUATIONAL_TAGS).map(([code, tag]) => (
            <div key={code} className="flex items-start gap-2">
              <span className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded border ${TAG_COLORS[code]}`}>
                [{code}]
              </span>
              <div>
                <p className="text-white text-xs font-medium">{tag.name}</p>
                <p className="text-zinc-500 text-[10px]">{tag.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ImageUploadSection = () => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      // Cookie sent automatically
      const response = await axios.get(`${API}/images`);
      setImages(response.data);
    } catch (error) {
      // Silently fail - images may not exist
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Image uploaded!");
      fetchImages();
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (filename) => {
    if (!confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/images/${filename}`);
      toast.success("Image deleted");
      fetchImages();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard!");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">Image Library</h2>
          <p className="text-zinc-500 text-sm mt-1">Upload images for Hammer Talk posts and banners</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            id="image-upload-input"
          />
          <Button 
            disabled={uploading} 
            className="btn-primary-glow"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Uploading..." : "Upload Image"}
          </Button>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="bg-hsl-surface border border-white/5 rounded-xl p-12 text-center">
          <Image className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">No images uploaded yet</p>
          <p className="text-zinc-600 text-sm mt-1">Upload images to use in your Hammer Talk posts</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => {
            // Images are now served via /api/images/view/filename
            const baseUrl = API.replace('/api', '');
            const imageUrl = `${baseUrl}${img.url}`;
            return (
              <div key={img.filename} className="bg-hsl-surface border border-white/5 rounded-xl overflow-hidden group">
                <div className="aspect-video bg-hsl-bg relative">
                  <img 
                    src={imageUrl} 
                    alt={img.filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => copyUrl(imageUrl)}
                      className="bg-hsl-primary text-black hover:bg-hsl-primary/80"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(img.filename)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-zinc-400 text-xs truncate">{img.filename}</p>
                  <button 
                    onClick={() => copyUrl(imageUrl)}
                    className="text-hsl-primary text-xs hover:underline mt-1 flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copy URL
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [notificationStats, setNotificationStats] = useState(null);
  const [clvStats, setClvStats] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ show_clv_stats: false });
  const [picks, setPicks] = useState([]);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [showPickForm, setShowPickForm] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPick, setEditingPick] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [siteUpdates, setSiteUpdates] = useState([]);
  const [newUpdate, setNewUpdate] = useState({ title: "", description: "", category: "update", date: "" });
  
  // Masterclass state
  const [masterclassEntries, setMasterclassEntries] = useState([]);
  const [showMasterclassForm, setShowMasterclassForm] = useState(false);
  const [editingMasterclass, setEditingMasterclass] = useState(null);
  const [masterclassForm, setMasterclassForm] = useState({
    date: "",
    title: "",
    units_profit: "",
    dollar_profit: "",
    picks_summary: "",
    analysis: "",
    image_url: "",
    rank: ""
  });
  const [uploadingMasterclassImage, setUploadingMasterclassImage] = useState(false);

  const getAuthHeaders = () => {
    const savedToken = token || localStorage.getItem("hsl_token");
    return savedToken ? { Authorization: `Bearer ${savedToken}` } : {};
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    if (!user.is_admin) {
      navigate("/");
      toast.error("Admin access required");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, picksRes, postsRes, usersRes, notifRes, clvRes, settingsRes, updatesRes, masterclassRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/picks?limit=1000`),
        axios.get(`${API}/blog?limit=100`),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/notification-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/stats/clv`).catch(() => ({ data: null })),
        axios.get(`${API}/settings/site`).catch(() => ({ data: { show_clv_stats: false } })),
        axios.get(`${API}/site-updates`).catch(() => ({ data: [] })),
        axios.get(`${API}/masterclass`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setPicks(picksRes.data);
      setPosts(postsRes.data);
      setUsers(usersRes.data);
      setNotificationStats(notifRes.data);
      setClvStats(clvRes.data);
      setSiteSettings(settingsRes.data);
      setSiteUpdates(updatesRes.data);
      setMasterclassEntries(masterclassRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `Sport,Game Time (ET),Matchup,Pick,Odds,Bet Amount,Analysis,Pick Tier,Tags
NFL,3/20/2026 1:00 PM,Bills @ Chiefs,Chiefs -3,-110,2000,FULL HAMMER. Mahomes at home is automatic.,Hammer,FAT|TRAV
NBA,3/20/2026 7:30 PM,Lakers @ Celtics,Over 225.5,-105,500,Both teams playing at high pace.,Members Only,PSY
NHL,3/20/2026 8:00 PM,Bruins @ Rangers,Rangers ML,+125,300,Value on the home team.,Free,STK|ENV
NCAAB,3/20/2026 6:00 PM,UNC @ Duke,Duke -4.5,-110,400,Tournament game. Blue Devils hot at home.,Scheduled Loss,PER`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'picks_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded!");
  };

  const handleDownloadScoresTemplate = () => {
    const csvContent = `Matchup,Away Score,Home Score
Bills @ Chiefs,17,24
Lakers @ Celtics,108,112
Bruins @ Rangers,2,3`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'scores_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Scores template downloaded!");
  };

  const handleScoresUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' };
      const response = await axios.post(`${API}/picks/upload-scores`, formData, { headers });
      
      toast.success(`${response.data.updated} picks updated with scores!`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.slice(0, 5).forEach(err => toast.error(err));
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload scores");
    }
    
    e.target.value = '';
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' };
      const response = await axios.post(`${API}/picks/upload-csv`, formData, { headers });
      
      toast.success(`${response.data.created} picks uploaded successfully!`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
    }
    
    // Reset input
    e.target.value = '';
  };

  const handleExportAllPicks = async () => {
    try {
      const headers = getAuthHeaders();
      
      // Check if running as PWA (standalone mode)
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    window.navigator.standalone === true;
      
      if (isPWA) {
        // For PWA, open in new browser tab to handle download properly
        const token = localStorage.getItem("hsl_token");
        const downloadUrl = `${API}/picks/export-csv?token=${encodeURIComponent(token)}`;
        window.open(downloadUrl, '_blank');
        toast.success("Opening download in browser...");
        return;
      }
      
      // Regular browser - use blob download
      const response = await axios.get(`${API}/picks/export-csv`, { 
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'hsl_picks_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Picks exported successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export picks");
    }
  };

  const handleExportExcel = async () => {
    try {
      const headers = getAuthHeaders();
      
      // Check if running as PWA (standalone mode)
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    window.navigator.standalone === true;
      
      if (isPWA) {
        // For PWA, open in new browser tab to handle download properly
        const token = localStorage.getItem("hsl_token");
        const downloadUrl = `${API}/picks/export-excel?token=${encodeURIComponent(token)}`;
        window.open(downloadUrl, '_blank');
        toast.success("Opening download in browser...");
        return;
      }
      
      // Regular browser - use blob download
      const response = await axios.get(`${API}/picks/export-excel`, { 
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'hsl_picks_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel exported with sport tabs!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export Excel");
    }
  };

  const handleSavePick = async (pickData) => {
    try {
      const headers = getAuthHeaders();
      if (editingPick) {
        await axios.put(`${API}/picks/${editingPick.id}`, pickData, { headers });
        toast.success("Pick updated");
      } else {
        await axios.post(`${API}/picks`, pickData, { headers });
        toast.success("Pick created");
      }
      setShowPickForm(false);
      setEditingPick(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pick");
    }
  };

  const handleDeletePick = async (pickId) => {
    if (!confirm("Delete this pick?")) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/picks/${pickId}`, { headers });
      toast.success("Pick deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete pick");
    }
  };

  // Get score for a specific pick (for grading) - ONLY API call for scores
  const [scoreLoading, setScoreLoading] = useState(null);
  const [pickScores, setPickScores] = useState({});
  
  const handleGetScore = async (pickId) => {
    setScoreLoading(pickId);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/pick/${pickId}/score`, { headers });
      if (response.data.found && response.data.score) {
        const score = response.data.score;
        setPickScores(prev => ({ ...prev, [pickId]: score }));
        
        // Use team1/team2 scores which match the pick's matchup order
        const scoreText = `${score.team1 || score.away_team}: ${score.team1_score ?? score.away_score} - ${score.team2 || score.home_team}: ${score.team2_score ?? score.home_score}`;
        
        if (score.completed) {
          // Auto-save the final score to the pick
          // CRITICAL: Use team1_score/team2_score which match the matchup order
          // away_score = first team in matchup, home_score = second team in matchup
          try {
            await axios.put(`${API}/picks/${pickId}`, {
              away_score: score.team1_score ?? score.away_score,
              home_score: score.team2_score ?? score.home_score
            }, { headers });
            toast.success(`Final Score Saved: ${scoreText}`);
            fetchData(); // Refresh to show updated scores
          } catch (saveError) {
            toast.success(`Final Score: ${scoreText}`);
            toast.error("Could not auto-save score - please save manually");
          }
        } else {
          toast.info(`Current Score: ${scoreText} (Game in progress - not saved)`);
        }
      } else {
        // Check if game is too old for API
        if (response.data.days_old && response.data.days_old > 3) {
          toast.error(`Game is ${response.data.days_old} days old - enter score manually (API only keeps 3 days)`, { duration: 5000 });
        } else {
          toast.error(response.data.message || "Score not found - try entering manually");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get score");
    } finally {
      setScoreLoading(null);
    }
  };

  const handleSavePost = async (postData) => {
    try {
      const headers = getAuthHeaders();
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, postData, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog`, postData, { headers });
        toast.success("Post created");
      }
      setShowPostForm(false);
      setEditingPost(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save post");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm("Delete this post?")) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/blog/${postId}`, { headers });
      toast.success("Post deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleUpdateUserTier = async (userId, tier) => {
    try {
      const headers = getAuthHeaders();
      await axios.patch(`${API}/admin/users/${userId}/tier?tier=${tier}`, {}, { headers });
      toast.success("User tier updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user tier");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-hsl-bg" data-testid="admin-page">
        <Navbar 
          onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
          onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
        />
        <div className="pt-24 text-center">
          <p className="text-zinc-400 text-lg">Please log in to access admin panel</p>
          <Button onClick={() => setAuthModalOpen(true)} className="btn-primary-glow mt-4">
            Login
          </Button>
        </div>
        <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} mode={authMode} setMode={setAuthMode} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-hsl-bg">
        <Navbar onLoginClick={() => {}} onRegisterClick={() => {}} />
        <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
          <div className="grid grid-cols-4 gap-6 mb-8">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="admin-page">
      <Navbar 
        onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
        onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
      />
      
      {/* Header */}
      <div className="border-b border-white/5 bg-hsl-surface/50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white" data-testid="admin-title">
            Admin Panel
          </h1>
          <p className="text-zinc-400 mt-2">Manage picks, posts, and users</p>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} />
            <StatCard icon={DollarSign} label="Premium Users" value={stats.premium_users} color="text-hsl-primary" />
            <StatCard icon={TrendingUp} label="Total Picks" value={stats.total_picks} />
            <StatCard icon={FileText} label="Hammer Talk Posts" value={stats.total_posts} />
            <StatCard 
              icon={Bell} 
              label="Email Subs" 
              value={notificationStats?.email_subscribers || 0} 
              color={notificationStats?.email_configured ? "text-green-400" : "text-zinc-500"}
            />
            <StatCard 
              icon={Bell} 
              label="SMS Subs" 
              value={notificationStats?.sms_subscribers || 0} 
              color={notificationStats?.sms_configured ? "text-blue-400" : "text-zinc-500"}
            />
          </div>
        )}
        
        {/* Notification Status */}
        <NotificationStatus 
          notificationStats={notificationStats} 
          onRefresh={fetchData}
        />

        {/* CLV Stats & Toggle */}
        <div className="mb-6 p-4 rounded-xl border bg-purple-500/5 border-purple-500/20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium">
                  Closing Line Value (CLV) Stats
                </p>
                <p className="text-zinc-500 text-sm">
                  {clvStats && clvStats.total_picks_with_clv > 0 ? (
                    <>
                      {clvStats.picks_beating_line}/{clvStats.total_picks_with_clv} picks beat the line ({clvStats.beat_percentage}%) • Avg CLV: <span className={clvStats.average_clv >= 0 ? 'text-green-400' : 'text-red-400'}>{clvStats.average_clv >= 0 ? '+' : ''}{clvStats.average_clv}</span>
                    </>
                  ) : (
                    'No picks with closing lines yet. Enter closing lines when editing picks.'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-400">Show publicly:</span>
              <Switch
                checked={siteSettings.show_clv_stats}
                onCheckedChange={async (checked) => {
                  try {
                    const headers = getAuthHeaders();
                    await axios.put(`${API}/settings/site`, { show_clv_stats: checked }, { headers });
                    setSiteSettings(prev => ({ ...prev, show_clv_stats: checked }));
                    toast.success(checked ? 'CLV stats now visible on site' : 'CLV stats hidden from site');
                  } catch (error) {
                    toast.error('Failed to update setting');
                  }
                }}
              />
            </div>
          </div>
        </div>

        <Tabs defaultValue="picks" className="space-y-6">
          <TabsList className="bg-hsl-surface border border-white/10">
            <TabsTrigger value="picks" className="data-[state=active]:bg-hsl-primary data-[state=active]:text-black">
              Picks ({picks.length})
            </TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-hsl-primary data-[state=active]:text-black">
              Hammer Talk ({posts.length})
            </TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-hsl-primary data-[state=active]:text-black">
              Images
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-hsl-primary data-[state=active]:text-black">
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="performance" className="data-[state=active]:bg-hsl-primary data-[state=active]:text-black">
              <BarChart3 className="w-4 h-4 mr-1" /> Performance
            </TabsTrigger>
            <TabsTrigger value="masterclass" className="data-[state=active]:bg-hsl-primary data-[state=active]:text-black">
              🏆 Masterclass
            </TabsTrigger>
            <TabsTrigger value="updates" className="data-[state=active]:bg-hsl-primary data-[state=active]:text-black">
              Site Updates
            </TabsTrigger>
          </TabsList>

          {/* Picks Tab */}
          <TabsContent value="picks" className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="font-heading text-2xl font-bold uppercase text-white">Manage Picks</h2>
                <p className="text-zinc-500 text-sm mt-1">
                  CSV "Pick Tier" options: <span className="text-zinc-400">Free</span>, <span className="text-hsl-primary">Members Only</span>, <span className="text-yellow-400">Hammer</span>, <span className="text-purple-400">Scheduled Loss</span>
                </p>
                <p className="text-zinc-500 text-sm">
                  Tags (pipe-separated): <span className="text-orange-400">FAT</span> | <span className="text-blue-400">TRAV</span> | <span className="text-purple-400">PSY</span> | <span className="text-red-400">STK</span> | <span className="text-green-400">ENV</span> | <span className="text-yellow-400">PER</span>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleExportAllPicks} variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
                <Button onClick={handleExportExcel} variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20">
                  <Download className="w-4 h-4 mr-2" /> Export Excel (by Sport)
                </Button>
                <Button onClick={handleDownloadTemplate} variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                  <Download className="w-4 h-4 mr-2" /> CSV Template
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                  />
                  <Button as="span" variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10 pointer-events-none">
                    <Upload className="w-4 h-4 mr-2" /> Upload Picks
                  </Button>
                </label>
                <Button onClick={handleDownloadScoresTemplate} variant="outline" className="bg-white/5 border-white/10 text-yellow-400 hover:bg-white/10">
                  <Download className="w-4 h-4 mr-2" /> Scores Template
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleScoresUpload}
                    className="hidden"
                  />
                  <Button as="span" variant="outline" className="bg-white/5 border-white/10 text-yellow-400 hover:bg-white/10 pointer-events-none">
                    <Upload className="w-4 h-4 mr-2" /> Upload Scores
                  </Button>
                </label>
                <Button onClick={() => { setEditingPick(null); setShowPickForm(true); }} className="btn-primary-glow">
                  <Plus className="w-4 h-4 mr-2" /> Add Pick
                </Button>
              </div>
            </div>
            
            <div className="bg-hsl-surface border border-white/5 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-white/5">
                    <tr className="text-left text-zinc-500 text-xs uppercase tracking-wide">
                      <th className="p-4">Sport</th>
                      <th className="p-4">Game Time</th>
                      <th className="p-4">Matchup</th>
                      <th className="p-4">Pick</th>
                      <th className="p-4">Odds</th>
                      <th className="p-4">Bet</th>
                      <th className="p-4">Pick Type</th>
                      <th className="p-4">Result</th>
                      <th className="p-4">Score</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {picks.map((pick) => (
                      <tr key={pick.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4 text-hsl-primary font-bold">{pick.sport}</td>
                        <td className="p-4 text-zinc-300 text-sm">
                          {new Date(pick.game_time).toLocaleDateString('en-US', { 
                            timeZone: 'America/New_York',
                            month: 'short', 
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })} ET
                        </td>
                        <td className="p-4 text-white">{pick.matchup}</td>
                        <td className="p-4 text-white font-mono">{pick.pick}</td>
                        <td className="p-4 text-zinc-400 font-mono">{pick.odds}</td>
                        <td className="p-4 text-hsl-primary font-mono">${pick.bet_amount}</td>
                        <td className="p-4">
                          <div className="flex flex-col gap-1">
                            {pick.pick_tier === 'hammer' ? (
                              <span className="text-yellow-400 font-bold text-xs">🔨 HAMMER</span>
                            ) : pick.pick_tier === 'scheduled_loss' ? (
                              <span className="text-purple-400 font-bold text-xs">📅 SCHEDULED LOSS</span>
                            ) : pick.pick_tier === 'premium' || pick.is_premium ? (
                              <span className="text-hsl-primary font-bold text-xs">MEMBERS ONLY</span>
                            ) : (
                              <span className="text-zinc-500 text-xs">FREE</span>
                            )}
                            {pick.is_prop && (
                              <span className="bg-purple-500/20 text-purple-400 text-[10px] px-1.5 py-0.5 rounded font-medium">PROP</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`text-xs font-bold uppercase ${
                            pick.result === 'win' ? 'text-green-400' : 
                            pick.result === 'loss' ? 'text-red-400' : 
                            'text-yellow-400'
                          }`}>{pick.result}</span>
                        </td>
                        <td className="p-4">
                          {/* Show fetched score if available, OR show stored score from database */}
                          {pickScores[pick.id] ? (
                            <div className="text-xs">
                              <span className={pickScores[pick.id].completed ? 'text-green-400' : 'text-yellow-400'}>
                                {pickScores[pick.id].team1_score ?? pickScores[pick.id].away_score} - {pickScores[pick.id].team2_score ?? pickScores[pick.id].home_score}
                              </span>
                              <span className="text-zinc-500 ml-1">
                                {pickScores[pick.id].completed ? 'FINAL' : 'LIVE'}
                              </span>
                            </div>
                          ) : pick.prop_result ? (
                            <div className="text-xs">
                              <span className="text-hsl-primary font-medium">{pick.prop_result}</span>
                            </div>
                          ) : (pick.away_score !== null && pick.away_score !== undefined && pick.home_score !== null && pick.home_score !== undefined) ? (
                            <div className="text-xs">
                              <span className="text-green-400">
                                {pick.away_score} - {pick.home_score}
                              </span>
                              <span className="text-zinc-500 ml-1">FINAL</span>
                            </div>
                          ) : (
                            <span className="text-zinc-600 text-xs">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {pick.result === 'pending' && (
                              <button 
                                onClick={() => handleGetScore(pick.id)} 
                                disabled={scoreLoading === pick.id}
                                className="text-zinc-400 hover:text-hsl-primary disabled:opacity-50"
                                title="Get final score for grading"
                              >
                                {scoreLoading === pick.id ? (
                                  <div className="w-4 h-4 border-2 border-hsl-primary border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Search className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <button onClick={() => { setEditingPick(pick); setShowPickForm(true); }} className="text-zinc-400 hover:text-white">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeletePick(pick.id)} className="text-zinc-400 hover:text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Hammer Talk Tab */}
          <TabsContent value="blog" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-2xl font-bold uppercase text-white">Manage Hammer Talk</h2>
              <Button onClick={() => { setEditingPost(null); setShowPostForm(true); }} className="btn-primary-glow">
                <Plus className="w-4 h-4 mr-2" /> Add Post
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {posts.map((post) => (
                <div key={post.id} className="bg-hsl-surface border border-white/5 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-xs font-bold uppercase text-hsl-primary">{post.category}</span>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingPost(post); setShowPostForm(true); }} className="text-zinc-400 hover:text-white">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeletePost(post.id)} className="text-zinc-400 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-white mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-zinc-500 text-sm line-clamp-2">{post.excerpt}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images" className="space-y-4">
            <ImageUploadSection />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <h2 className="font-heading text-2xl font-bold uppercase text-white">Manage Users</h2>
            
            <div className="bg-hsl-surface border border-white/5 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-white/5">
                    <tr className="text-left text-zinc-500 text-xs uppercase tracking-wide">
                      <th className="p-4">Name</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Tier</th>
                      <th className="p-4">Admin</th>
                      <th className="p-4">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4 text-white font-semibold">{u.name}</td>
                        <td className="p-4 text-zinc-400">{u.email}</td>
                        <td className="p-4">
                          <Select value={u.subscription_tier} onValueChange={(v) => handleUpdateUserTier(u.id, v)}>
                            <SelectTrigger className="w-32 h-8 bg-hsl-bg border-white/10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="premium">Members Only</SelectItem>
                              <SelectItem value="vip">VIP</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4">{u.is_admin ? <Check className="w-4 h-4 text-hsl-primary" /> : "-"}</td>
                        <td className="p-4 text-zinc-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <div>
              <h2 className="font-heading text-2xl font-bold uppercase text-white">Performance Audit</h2>
              <p className="text-zinc-500 text-sm mt-1">Track ROI by situational edge factors</p>
            </div>
            <PerformanceAudit />
          </TabsContent>

          {/* Masterclass Tab */}
          <TabsContent value="masterclass" className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="font-heading text-2xl font-bold uppercase text-white">HSL Masterclass</h2>
                <p className="text-zinc-500 text-sm mt-1">Manage top 20 daily profit days (ranked by units)</p>
              </div>
              <Button
                onClick={() => {
                  setEditingMasterclass(null);
                  setMasterclassForm({
                    date: new Date().toISOString().split('T')[0],
                    title: "",
                    units_profit: "",
                    dollar_profit: "",
                    picks_summary: "",
                    analysis: "",
                    image_url: "",
                    rank: masterclassEntries.length + 1
                  });
                  setShowMasterclassForm(true);
                }}
                className="bg-yellow-500 hover:bg-yellow-400 text-black"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Masterclass Day
              </Button>
            </div>

            {/* Masterclass Form */}
            {showMasterclassForm && (
              <div className="bg-hsl-surface border border-white/10 rounded-xl p-6">
                <h3 className="font-heading text-xl font-bold text-white mb-4">
                  {editingMasterclass ? "Edit Masterclass Entry" : "Add New Masterclass Day"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Date</Label>
                    <Input
                      type="date"
                      value={masterclassForm.date}
                      onChange={(e) => setMasterclassForm({ ...masterclassForm, date: e.target.value })}
                      className="input-hsl"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Title (e.g., "5-0 Sweep Day")</Label>
                    <Input
                      value={masterclassForm.title}
                      onChange={(e) => setMasterclassForm({ ...masterclassForm, title: e.target.value })}
                      className="input-hsl"
                      placeholder="Perfect Sunday"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Units Profit</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={masterclassForm.units_profit}
                      onChange={(e) => setMasterclassForm({ ...masterclassForm, units_profit: e.target.value })}
                      className="input-hsl"
                      placeholder="5.5"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Dollar Profit</Label>
                    <Input
                      type="number"
                      value={masterclassForm.dollar_profit}
                      onChange={(e) => setMasterclassForm({ ...masterclassForm, dollar_profit: e.target.value })}
                      className="input-hsl"
                      placeholder="2750"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Picks Summary (e.g., "5-0 | NBA: 3-0, MLB: 2-0")</Label>
                    <Input
                      value={masterclassForm.picks_summary}
                      onChange={(e) => setMasterclassForm({ ...masterclassForm, picks_summary: e.target.value })}
                      className="input-hsl"
                      placeholder="5-0 | NBA: 3-0, MLB: 2-0"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Rank (1-20)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={masterclassForm.rank}
                      onChange={(e) => setMasterclassForm({ ...masterclassForm, rank: e.target.value })}
                      className="input-hsl"
                      placeholder="1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-zinc-400">Image</Label>
                    <div className="flex gap-2">
                      <Input
                        value={masterclassForm.image_url}
                        onChange={(e) => setMasterclassForm({ ...masterclassForm, image_url: e.target.value })}
                        className="input-hsl flex-1"
                        placeholder="Image URL or upload"
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setUploadingMasterclassImage(true);
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const res = await axios.post(`${API}/admin/upload-cloudinary`, formData, {
                                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
                              });
                              setMasterclassForm({ ...masterclassForm, image_url: res.data.url });
                              toast.success("Image uploaded!");
                            } catch (error) {
                              toast.error("Upload failed");
                            } finally {
                              setUploadingMasterclassImage(false);
                            }
                          }}
                        />
                        <Button type="button" variant="outline" className="bg-white/5 border-white/10" disabled={uploadingMasterclassImage}>
                          {uploadingMasterclassImage ? "..." : <Upload className="w-4 h-4" />}
                        </Button>
                      </label>
                    </div>
                    {masterclassForm.image_url && (
                      <img src={masterclassForm.image_url} alt="Preview" className="mt-2 h-32 rounded-lg object-cover" />
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-zinc-400">Analysis</Label>
                    <textarea
                      value={masterclassForm.analysis}
                      onChange={(e) => setMasterclassForm({ ...masterclassForm, analysis: e.target.value })}
                      className="input-hsl w-full min-h-[120px]"
                      placeholder="Detailed breakdown of the picks and why they hit..."
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={async () => {
                      try {
                        const headers = getAuthHeaders();
                        const payload = {
                          ...masterclassForm,
                          units_profit: parseFloat(masterclassForm.units_profit),
                          dollar_profit: parseFloat(masterclassForm.dollar_profit),
                          rank: parseInt(masterclassForm.rank)
                        };
                        if (editingMasterclass) {
                          await axios.put(`${API}/masterclass/${editingMasterclass.id}`, payload, { headers });
                          toast.success("Masterclass entry updated!");
                        } else {
                          await axios.post(`${API}/masterclass`, payload, { headers });
                          toast.success("Masterclass entry created!");
                        }
                        setShowMasterclassForm(false);
                        fetchData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to save");
                      }
                    }}
                    className="bg-yellow-500 hover:bg-yellow-400 text-black"
                  >
                    {editingMasterclass ? "Update Entry" : "Add Entry"}
                  </Button>
                  <Button
                    onClick={() => setShowMasterclassForm(false)}
                    variant="outline"
                    className="bg-white/5 border-white/10"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Masterclass Entries List */}
            <div className="space-y-4">
              {masterclassEntries.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  No masterclass entries yet. Add your top profit days!
                </div>
              ) : (
                masterclassEntries.map((entry) => (
                  <div key={entry.id} className="bg-hsl-surface border border-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                      entry.rank === 1 ? 'bg-yellow-500 text-black' :
                      entry.rank === 2 ? 'bg-zinc-300 text-black' :
                      entry.rank === 3 ? 'bg-amber-600 text-white' :
                      'bg-hsl-bg text-white'
                    }`}>
                      #{entry.rank}
                    </div>
                    {entry.image_url && (
                      <img src={entry.image_url} alt="" className="w-20 h-14 rounded-lg object-cover" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-white">{entry.title}</h4>
                        <span className="text-zinc-500 text-sm">{entry.date}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-400 font-mono">+{entry.units_profit}u</span>
                        <span className="text-green-400 font-mono">+${entry.dollar_profit?.toLocaleString()}</span>
                        <span className="text-zinc-500">{entry.picks_summary}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-white/5 border-white/10"
                        onClick={() => {
                          setEditingMasterclass(entry);
                          setMasterclassForm({
                            date: entry.date,
                            title: entry.title,
                            units_profit: entry.units_profit,
                            dollar_profit: entry.dollar_profit,
                            picks_summary: entry.picks_summary,
                            analysis: entry.analysis,
                            image_url: entry.image_url || "",
                            rank: entry.rank
                          });
                          setShowMasterclassForm(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
                        onClick={async () => {
                          if (window.confirm("Delete this masterclass entry?")) {
                            try {
                              const headers = getAuthHeaders();
                              await axios.delete(`${API}/masterclass/${entry.id}`, { headers });
                              toast.success("Entry deleted");
                              fetchData();
                            } catch (error) {
                              toast.error("Failed to delete");
                            }
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Site Updates Tab */}
          <TabsContent value="updates" className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="font-heading text-2xl font-bold uppercase text-white">Site Updates</h2>
                <p className="text-zinc-500 text-sm mt-1">Add changelog entries to show site improvements</p>
              </div>
              <Button
                onClick={async () => {
                  try {
                    const headers = getAuthHeaders();
                    const response = await axios.post(`${API}/site-updates/seed-history`, {}, { headers });
                    toast.success(response.data.message);
                    // Refresh updates list
                    const updatesRes = await axios.get(`${API}/site-updates`);
                    setSiteUpdates(updatesRes.data);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to seed history");
                  }
                }}
                variant="outline"
                className="bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
              >
                <Download className="w-4 h-4 mr-2" /> Load Historical Updates
              </Button>
            </div>
            
            {/* Add New Update Form */}
            <div className="bg-hsl-surface rounded-xl border border-white/10 p-6">
              <h3 className="text-white font-semibold mb-4">Add New Update</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-zinc-400">Title</Label>
                    <Input
                      value={newUpdate.title}
                      onChange={(e) => setNewUpdate({...newUpdate, title: e.target.value})}
                      placeholder="e.g., Added Situational Edge Tracking"
                      className="input-hsl mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Category</Label>
                    <Select 
                      value={newUpdate.category} 
                      onValueChange={(v) => setNewUpdate({...newUpdate, category: v})}
                    >
                      <SelectTrigger className="input-hsl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="feature">New Feature</SelectItem>
                        <SelectItem value="improvement">Improvement</SelectItem>
                        <SelectItem value="fix">Bug Fix</SelectItem>
                        <SelectItem value="update">Update</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Date (optional)</Label>
                    <Input
                      type="date"
                      value={newUpdate.date}
                      onChange={(e) => setNewUpdate({...newUpdate, date: e.target.value})}
                      className="input-hsl mt-1"
                      placeholder="Leave blank for today"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={async () => {
                        if (!newUpdate.title || !newUpdate.description) {
                          toast.error("Please fill in title and description");
                          return;
                        }
                        try {
                          const headers = getAuthHeaders();
                          const payload = {
                            title: newUpdate.title,
                            description: newUpdate.description,
                            category: newUpdate.category
                          };
                          // Add custom date if provided
                          if (newUpdate.date) {
                            payload.created_at = new Date(newUpdate.date + "T12:00:00Z").toISOString();
                          }
                          await axios.post(`${API}/site-updates`, payload, { headers });
                          toast.success("Update added!");
                          setNewUpdate({ title: "", description: "", category: "update", date: "" });
                          fetchData();
                        } catch (error) {
                          toast.error("Failed to add update");
                        }
                      }}
                      className="btn-primary-glow w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Update
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400">Description</Label>
                  <Textarea
                    value={newUpdate.description}
                    onChange={(e) => setNewUpdate({...newUpdate, description: e.target.value})}
                    placeholder="Describe what was added or changed..."
                    className="input-hsl mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Existing Updates List */}
            <div className="bg-hsl-surface rounded-xl border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h3 className="text-white font-semibold">Recent Updates ({siteUpdates.length})</h3>
              </div>
              {siteUpdates.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  No updates yet. Add your first one above!
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {siteUpdates.map((update) => (
                    <div key={update.id} className="p-4 flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            update.category === 'feature' ? 'bg-green-500/20 text-green-400' :
                            update.category === 'improvement' ? 'bg-blue-500/20 text-blue-400' :
                            update.category === 'fix' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {update.category === 'feature' ? 'Feature' :
                             update.category === 'improvement' ? 'Improvement' :
                             update.category === 'fix' ? 'Bug Fix' : 'Update'}
                          </span>
                          <span className="text-zinc-500 text-xs">
                            {new Date(update.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-white font-medium">{update.title}</p>
                        <p className="text-zinc-400 text-sm mt-1">{update.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (window.confirm("Delete this update?")) {
                            try {
                              const headers = getAuthHeaders();
                              await axios.delete(`${API}/site-updates/${update.id}`, { headers });
                              toast.success("Update deleted");
                              fetchData();
                            } catch (error) {
                              toast.error("Failed to delete");
                            }
                          }
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Pick Form Modal */}
      <Dialog open={showPickForm} onOpenChange={setShowPickForm}>
        <DialogContent className="bg-hsl-surface border border-white/10 sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase tracking-tight">
              {editingPick ? "Edit Pick" : "New Pick"}
            </DialogTitle>
          </DialogHeader>
          <PickForm 
            key={editingPick?.id || 'new'}
            pick={editingPick} 
            onSave={handleSavePick} 
            onCancel={() => { setShowPickForm(false); setEditingPick(null); }} 
          />
        </DialogContent>
      </Dialog>

      {/* Hammer Talk Form Modal */}
      <Dialog open={showPostForm} onOpenChange={setShowPostForm}>
        <DialogContent className="bg-hsl-surface border border-white/10 sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase tracking-tight">
              {editingPost ? "Edit Post" : "New Post"}
            </DialogTitle>
          </DialogHeader>
          <BlogForm 
            post={editingPost} 
            onSave={handleSavePost} 
            onCancel={() => { setShowPostForm(false); setEditingPost(null); }} 
          />
        </DialogContent>
      </Dialog>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} mode={authMode} setMode={setAuthMode} />
    </div>
  );
};

export default AdminPage;
